import React, { useState, useEffect, useRef } from 'react';
import { Story } from '../types';
import { theme } from '../../../styles/theme';
import styled from 'styled-components';

interface StoryViewerProps {
  isOpen: boolean;
  stories: Story[];
  initialIndex?: number;
  onClose: () => void;
  userId?: string;
  userName?: string;
  onSwipeUp?: (userId: string) => void; // Callback for swipe-up gesture
  onProfileClick?: (userId: string) => void; // New: Callback for profile navigation
  getUserName?: (userId: string) => string; // Function to get user name from userId
  // Fires once per Moment when it finishes or is skipped past, so the caller
  // can record the view.
  onMomentViewed?: (story: Story) => void;
  // The endless chain: when this person's Moments run out, hand off to the next
  // ranked person instead of closing. Without these the viewer dead-ends back
  // at the grid after every reel, which is where people stop watching.
  onNextUser?: () => void;
  onPrevUser?: () => void;
  hasNextUser?: boolean;
  // Holds the reel while something is layered over it — the profile drawer, for
  // instance. Without this the clip keeps playing and the progress timer keeps
  // advancing behind the overlay, so a viewer reading someone's profile comes
  // back to a different Moment, or to a closed viewer.
  paused?: boolean;
}

const ViewerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 10001;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StoryVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

const ProgressBarContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.xs};
  z-index: 10002;
`;

const ProgressBar = styled.div<{ $progress: number; $isActive: boolean }>`
  flex: 1;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background: white;
    transition: ${props => props.$isActive ? 'width 0.1s linear' : 'none'};
  }
`;

const StoryHeader = styled.div`
  position: absolute;
  top: ${theme.spacing.md};
  left: 0;
  right: 0;
  padding: ${theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10002;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), transparent);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: white;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  border: 2px solid white;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const TimeAgo = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
`;

const CloseButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const NavigationArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  z-index: 10001;
`;

const LeftArea = styled.div`
  flex: 1;
  cursor: pointer;
`;

const RightArea = styled.div`
  flex: 1;
  cursor: pointer;
`;

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return 'Just now';
  }
};

export const StoryViewer: React.FC<StoryViewerProps> = ({
  isOpen,
  stories,
  initialIndex = 0,
  onClose,
  userId,
  userName,
  onSwipeUp,
  onProfileClick,
  getUserName,
  onMomentViewed,
  onNextUser,
  onPrevUser,
  hasNextUser = false,
  paused = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(15); // Default to 15 seconds
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  
  // Swipe detection refs
  const touchStartY = useRef<number>(0);
  const touchStartX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const SWIPE_THRESHOLD = 50; // Minimum distance for swipe

  const currentStory = stories[currentIndex];
  
  // Get current user info from the current story
  const getCurrentUserInfo = () => {
    if (!currentStory) return { userId: userId || '', userName: userName || 'User' };
    const storyUserId = currentStory.userId;
    const storyUserName = getUserName 
      ? getUserName(storyUserId) 
      : (userName || 'User');
    return { userId: storyUserId, userName: storyUserName };
  };
  
  const currentUserInfo = getCurrentUserInfo();

  useEffect(() => {
    if (isOpen && currentStory) {
      setProgress(0);
      setIsPlaying(false);
      startTimeRef.current = Date.now();
      
      // Auto-play video
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
            // Get actual video duration
            if (videoRef.current) {
              setVideoDuration(videoRef.current.duration || 15);
            }
            startProgress();
          })
          .catch(err => {
            console.error('Video autoplay prevented:', err);
          });
      }
    }

    return () => {
      stopProgress();
    };
  }, [isOpen, currentIndex, currentStory]);

  useEffect(() => {
    if (!isOpen) {
      stopProgress();
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [isOpen]);

  // Hold and release the reel when something is layered over it. Resuming
  // rather than restarting means the viewer returns to the same point in the
  // clip they left.
  useEffect(() => {
    const video = videoRef.current;
    if (!isOpen || !video) return;

    if (paused) {
      video.pause();
      setIsPlaying(false);
      stopProgress();
    } else {
      video.play()
        .then(() => {
          setIsPlaying(true);
          startProgress();
        })
        .catch(() => { /* autoplay blocked — the frame stays put */ });
    }
    // Intentionally keyed on the pause state alone: startProgress/stopProgress
    // are recreated every render, so listing them would restart the clip on
    // each one.
  }, [paused, isOpen]);

  const startProgress = () => {
    stopProgress();
    progressIntervalRef.current = setInterval(() => {
      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration || videoDuration;
        const newProgress = (currentTime / duration) * 100;
        setProgress(Math.min(newProgress, 100));

        // Auto-advance to next story when video ends
        if (videoRef.current.ended || currentTime >= duration) {
          handleNext();
        }
      }
    }, 100);
  };

  const stopProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  // Reported at most once per Moment. The progress interval can fire several
  // times past the end of a clip before the index actually moves, so without
  // this guard a single Moment would report itself viewed repeatedly.
  const viewedRef = useRef<Set<string>>(new Set());

  const reportViewed = (story?: Story) => {
    if (!story || !onMomentViewed) return;
    if (viewedRef.current.has(story.id)) return;
    viewedRef.current.add(story.id);
    onMomentViewed(story);
  };

  const handleNext = () => {
    reportViewed(currentStory);

    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
      return;
    }

    // End of this person's Moments. Continue into the next person's rather than
    // dropping the viewer back to the grid — closing here is what turns a feed
    // into a series of dead ends.
    if (onNextUser && hasNextUser) {
      stopProgress();
      onNextUser();
      return;
    }

    onClose();
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setProgress(0);
      return;
    }

    // Tapping back from the first Moment goes to the previous person, matching
    // the forward direction of the chain.
    if (onPrevUser) {
      stopProgress();
      onPrevUser();
      return;
    }

    onClose();
  };

  const handleVideoEnd = () => {
    handleNext();
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
        startProgress();
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        stopProgress();
      }
    }
  };

  // Touch handlers for swipe-up gesture
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent default to avoid scrolling
    e.preventDefault();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndY.current = e.changedTouches[0].clientY;
    touchEndX.current = e.changedTouches[0].clientX;
    
    const deltaY = touchStartY.current - touchEndY.current; // Positive = swipe up
    const deltaX = Math.abs(touchStartX.current - touchEndX.current);
    
    // Check if it's a vertical swipe (not horizontal)
    if (Math.abs(deltaY) > deltaX && Math.abs(deltaY) > SWIPE_THRESHOLD) {
      if (deltaY > 0) {
        // Swipe up detected
        if (onSwipeUp && currentStory) {
          onSwipeUp(currentStory.userId);
        }
      }
    }
  };

  if (!isOpen || !currentStory) {
    return null;
  }

  return (
    <ViewerContainer 
      $isOpen={isOpen}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <ProgressBarContainer>
        {stories.map((story, index) => (
          <ProgressBar
            key={story.id}
            $progress={index === currentIndex ? progress : index < currentIndex ? 100 : 0}
            $isActive={index === currentIndex && isPlaying}
          />
        ))}
      </ProgressBarContainer>

      <StoryHeader>
        <UserInfo>
          <UserAvatar>
            {currentUserInfo.userName ? currentUserInfo.userName.charAt(0).toUpperCase() : '👤'}
          </UserAvatar>
          <div>
            <UserName 
              onClick={() => {
                if (onProfileClick && currentUserInfo.userId) {
                  onProfileClick(currentUserInfo.userId);
                }
              }}
            >
              {currentUserInfo.userName}
            </UserName>
            <TimeAgo>{formatTimeAgo(currentStory.createdAt)}</TimeAgo>
          </div>
        </UserInfo>
        <CloseButton onClick={onClose}>×</CloseButton>
      </StoryHeader>

      <StoryVideo
        ref={videoRef}
        src={currentStory.videoUrl}
        autoPlay
        playsInline
        muted={false}
        onEnded={handleVideoEnd}
        onClick={handleVideoClick}
        onLoadedMetadata={(e) => {
          const duration = e.currentTarget.duration;
          setVideoDuration(duration || 15);
        }}
        onPlay={() => {
          setIsPlaying(true);
          startProgress();
        }}
        onPause={() => {
          setIsPlaying(false);
          stopProgress();
        }}
      />

      <NavigationArea>
        <LeftArea onClick={handlePrevious} />
        <RightArea onClick={handleNext} />
      </NavigationArea>
    </ViewerContainer>
  );
};

